import { sortNumber } from "../website/Pages/Reports/utils"
import { ITarget, ITargetsJSON } from "../website/types/schema"

export type DashboardData = {
  tags: TagCatalog
  thisMonthByTags: TagDataMap<IStat>
  lastMonthToDateByTags: TagDataMap<IStat>
  last12MonthsByTags: TagDataMap<IStat>[]
  customersJobValue: {
    name: string
    tagValueKey: string
    new: boolean
    c1: number
    c3: number
    c6: number
    c12: number
    r1: number
    r3: number
    r6: number
    r12: number
    id: string
  }[]
  customerChanges: {
    name: string
    tagValueKey: string
    new: boolean
    r1: number
    d1: number
    r3: number
    d3: number
    r6: number
    d6: number
    r12: number
    d12: number
    customerSpendingStopped: boolean
    avgSpendBeforeStop: number
    cnR1: number
    cnRV1: number
    cnR3: number
    cnRV3: number
    cnR6: number
    cnRV6: number
    cnR12: number
    cnRV12: number
  }[]
  topLinesOfBusiness: {
    name: string
    sumTotal: TagDataMap<number>
  }[]
  topBrands: {
    name: string
    totalSpend: TagDataMap<number>
  }[]
  dailyGrossMarginDataByMonth: {
    start: Date
    end: Date
    monthNumber: number
    monthRaw: number
    // This is for each day in the month
    data: IDailyData[]
  }[]
  dailyRevenueDataByMonth: {
    start: Date
    end: Date
    monthNumber: number
    monthRaw: number
    // This is for each day in the month
    data: IDailyData[]
  }[]
  businessMonthlyRevenueData: IDailyData[]
  lapsedMonthlyRevenueData: IDailyData[]
  microsoftEvents: { monthRaw: number; events: DashboardDataMicrosoftEvent[]; target: number }[]
}

export type IStat = {
  quoteCount: number
  orderCount: number
  revenue: number
  monthRaw: number
  grossMargin: number
  marginPercentage: number
  newBusinessRevenue: number
  lapsedBusinessRevenue: number
  rebate: number
}

export type IDailyData = {
  dateRaw: number
  target: number | null
  values: TagDataMap<number>
}

export type DashboardDataMicrosoftEvent = {
  createdDateTime: Date
  userId: string
  tagValueKey: string
  customerId: string
}

/** Returns the target value from targetsJSON data
 * @param type - the key of targetsJSON, e.g revenueTarget, grossTarget, etc
 * @param targets - the targetsJSON data, if not supplied will return null
 * @param month - the month number, if not supplied will return the current month. value from 0 - 11
 */
export function getTargetForMonth(
  type: keyof ITargetsJSON,
  targets?: ITargetsJSON,
  month?: number,
) {
  if (!targets) {
    return null
  }
  let monthNumber = month?.toString() as keyof ITarget | undefined
  if (!month && month !== 0) {
    monthNumber = new Date().getMonth().toString() as keyof ITarget
  }
  return targets?.[type]?.[monthNumber!] ?? null
}

export function customerChangeKey(
  diffOn: "30" | "quarter" | "halfYear" | "year",
  type: "pct" | "revenue" | "count",
) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  const key = `${type}_${diffOn}` as const
  return (
    {
      revenue_30: "r1",
      revenue_quarter: "r3",
      revenue_halfYear: "r6",
      revenue_year: "r12",
      count_30: "c1",
      count_quarter: "c3",
      count_halfYear: "c6",
      count_year: "c12",
      pct_30: "d1",
      pct_quarter: "d3",
      pct_halfYear: "d6",
      pct_year: "d12",
    } as const
  )[key]
}

export function sortCustomerChanges(
  data: (DashboardData["customerChanges"][number] | DashboardData["customersJobValue"][number])[],
  diffOn: "30" | "quarter" | "halfYear" | "year",
  type: "pct" | "revenue" | "count",
  direction: "ASC" | "DESC",
  opts?: { newBusiness?: boolean; filter?: boolean },
) {
  const keyToUse = customerChangeKey(diffOn, type) as keyof (typeof data)[0]

  const dataToSort = data.filter(item => {
    if (opts?.newBusiness && !item.new) {
      return false
    }
    if (opts?.filter) {
      if (direction === "DESC" && Number(item[keyToUse]) <= 0) {
        return false
      }
      if (direction !== "DESC" && Number(item[keyToUse]) >= 0) {
        return false
      }
    }
    return true
  })

  return sortNumber(dataToSort, keyToUse, direction)
}

// MARK: Filter Tags

// List of all available tags and their possible values
type TagCatalog = { key: string; values: string[] }[]

// Encoded form of a tag or tag group, used for lookup and filtering
type TagHash = string

// Map of tag hashes to associated data (e.g. totals)
export type TagDataMap<T> = Record<TagHash, T>

// A selected filter value from the user (e.g. form input)
export type TagFilter = { key: string; value: string | null }

// A tag assigned to a record (e.g. customer or invoice)
type TagValue = { key: string; value: string }

// Encodes a list of tag values into a hash string
export function hashTagValues(tagValues: TagValue[]): TagHash {
  tagValues.sort((a, b) => {
    const keyCompare = a.key.localeCompare(b.key)
    if (keyCompare !== 0) {
      return keyCompare
    }
    return a.value.localeCompare(b.value)
  })
  return tagValues.map(tag => `k[${tag.key}]=v[${tag.value}]`).join(";")
}

// Merges multiple tag hashes into a single combined hash
export function mergeTagHashes(hashes: TagHash[]): TagHash {
  // Sort the tag parts to ensure consistent ordering
  return hashes.sort().join(";")
}

// Checks if a tag hash matches a given filter
export function tagHashMatchesFilter(hash: TagHash, filter: TagFilter | null | undefined) {
  if (!filter) {
    return true
  } else if (filter.value) {
    return hash.includes(`k[${filter.key}]=v[${filter.value}]`)
  } else {
    return hash.includes(`k[${filter.key}]`)
  }
}

// Ensures a tag bucket exists in the data map, initializing it if needed
export function ensureTag<T>(data: TagDataMap<T>, hash: TagHash, defaultValue: T) {
  data[hash] ??= defaultValue
  return data[hash]
}

// Reduces data by applying a filter and merging matched values
export function collapseTagDataMap<T>(
  data: TagDataMap<T>,
  filter: TagFilter | null | undefined,
  merge: (arr: T[]) => T,
): T {
  let toCombine = Object.values(data)
  if (filter) {
    toCombine = Object.entries(data)
      .filter(([serial]) => tagHashMatchesFilter(serial, filter))
      .map(([_, value]) => value)
  }
  return merge(toCombine)
}

export function collapseTagDataMapForNumber(data: TagDataMap<number>, filter?: TagFilter | null) {
  return collapseTagDataMap(data, filter, sum)
}

function sum(arr: number[]) {
  let total = 0
  for (const value of arr) {
    total += value
  }
  return total
}

export function collapseTagDataMapForStats(data: TagDataMap<IStat>, filter?: TagFilter | null) {
  return collapseTagDataMap(data, filter, combineStats)
}

function combineStats(stats: IStat[]) {
  const combined: IStat = {
    quoteCount: 0,
    orderCount: 0,
    revenue: 0,
    monthRaw: 0,
    grossMargin: 0,
    marginPercentage: 0,
    newBusinessRevenue: 0,
    lapsedBusinessRevenue: 0,
    rebate: 0,
  }
  stats.forEach(stat => {
    combined.quoteCount += stat.quoteCount
    combined.orderCount += stat.orderCount
    combined.revenue += stat.revenue
    combined.monthRaw = Math.max(combined.monthRaw, stat.monthRaw)
    combined.grossMargin += stat.grossMargin
    combined.marginPercentage += stat.marginPercentage
    combined.newBusinessRevenue += stat.newBusinessRevenue
    combined.lapsedBusinessRevenue += stat.lapsedBusinessRevenue
    combined.rebate += stat.rebate
  })
  if (combined.revenue !== 0) {
    combined.marginPercentage = (combined.grossMargin * 100) / combined.revenue
  }
  return combined
}
